import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "react-query";
import { reactQueryGet } from "@/react/helpers/backendRequestHelpers";
import { aiSummaries } from "@/react/helpers/urlHelpers/internalApi/homePagePosts";
import type { SpaceDateRange } from "@/react/types/AiSummary/SpaceSummary";
import { minimumDelay } from "@circle-react/helpers/dateTimeHelpers";
import type { PostSpaceSummary } from "@circle-react/types/AiSummary/SpaceSummary";

export const REQUEST_KEY = "feed-summary";

interface ErrorResponse {
  body: {
    error_details?: {
      noPostsToSummarize?: boolean;
    };
  };
  status: number;
}

interface UseFeedSummaryProps {
  dateFrom: string;
  dateRange: SpaceDateRange;
  enabled: boolean;
}

export const useFeedSummary = ({
  dateFrom,
  enabled,
  dateRange,
}: UseFeedSummaryProps) => {
  const [hasDelayedNoPostsError, setHasDelayedNoPostsError] = useState(false);
  const queryKey = [REQUEST_KEY, dateRange];
  const queryClient = useQueryClient();
  const { data, isLoading, error, refetch } = useQuery<
    PostSpaceSummary,
    ErrorResponse
  >(
    queryKey,
    () =>
      reactQueryGet(
        aiSummaries.index({
          params: {
            date_from: dateFrom,
          },
        }),
      ),
    {
      enabled,
      staleTime: 10 * 60 * 1000, // 10 minutes
      retry: (_, error: any) =>
        error?.status !== 404 && error?.status !== 401 && error?.status !== 400,
    },
  );

  const clearCache = () => {
    void queryClient.resetQueries({ queryKey, exact: true });
  };

  useEffect(() => {
    const hasNoPostsError = error?.body?.error_details?.noPostsToSummarize;

    if (hasNoPostsError) {
      void minimumDelay(Promise.resolve(), 3000).then(() => {
        setHasDelayedNoPostsError(true);
      });
    } else {
      setHasDelayedNoPostsError(false);
    }
  }, [error]);

  return {
    spaceSummary: data,
    isLoadingSpaceSummary: isLoading,
    refetchSpaceSummary: refetch,
    clearSpaceSummary: clearCache,
    isNoPostsError: hasDelayedNoPostsError,
  };
};
